import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import {
  LocationProvider,
  useLocation as useLocationContext,
} from "./contexts/LocationContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ConfirmProvider } from "./contexts/ConfirmContext";
import { SetupWizardProvider } from "./contexts/SetupWizardContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import ClassesPanel from "./pages/ClassesPanel";
import Reservations from "./pages/Reservations";
import ReservationsCalendar from "./pages/ReservationsCalendar";
import Customers from "./pages/Customers";
import Settings from "./pages/Settings";
import Integration from "./pages/Integration";
import Notifications from "./pages/Notifications";
import NotificationsHistory from "./pages/NotificationsHistory";
import Schedule from "./pages/Schedule";
import TableMap from "./pages/TableMap";
import TableCombinations from "./pages/TableCombinations";
import Menus from "./pages/Menus";
import ClassesPage from "./pages/Classes";
import AdminClassesModule from "./pages/admin/ClassesModule";
import Services from "./pages/Services";
import Specialists from "./pages/Specialists";
import SpecialistSchedule from "./pages/SpecialistSchedule";
import AppointmentRooms from "./pages/AppointmentRooms";
import AppointmentRoomSchedule from "./pages/AppointmentRoomSchedule";
import Appointments from "./pages/Appointments";
import AppointmentSettings from "./pages/AppointmentSettings";
import AppointmentsGuide from "./pages/AppointmentsGuide";
import LocationSettings from "./pages/LocationSettings";
import Subscription from "./pages/Subscription";
import Team from "./pages/Team";
import Locations from "./pages/Locations";
import HelpCenter from "./pages/HelpCenter";
import TicketDetail from "./pages/TicketDetail";
import AdminTickets from "./pages/admin/Tickets";
import VoiceAI from "./pages/VoiceAI";
import Conversations from "./pages/Conversations";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import ConfirmEmailChange from "./pages/ConfirmEmailChange";
import AuthCallback from "./pages/AuthCallback";
import AcceptInvitation from "./pages/AcceptInvitation";
import Organizations from "./pages/admin/Organizations";
import ClientsHealth from "./pages/admin/ClientsHealth";
import Analytics from "./pages/admin/Analytics";
import Plans from "./pages/admin/Plans";
import UsersAdmin from "./pages/admin/Users";
import EndUsersAdmin from "./pages/admin/EndUsers";
import AppVisibilityAdmin from "./pages/admin/AppVisibility";
import ReservationStatuses from "./pages/admin/ReservationStatuses";
import ImpersonationLogs from "./pages/admin/ImpersonationLogs";
import AdminTestimonials from "./pages/admin/Testimonials";
import AdminPublicProfiles from "./pages/admin/PublicProfiles";
import AdminPublicProfileDetail from "./pages/admin/PublicProfileDetail";
import AdminEmails from "./pages/admin/Emails";
import AdminWhatsApp from "./pages/admin/WhatsApp";
import AdminWhatsAppAi from "./pages/admin/WhatsAppAi";
import AdminPlatformSupport from "./pages/admin/PlatformSupport";
import AdminSocialNetworks from "./pages/admin/SocialNetworks";
import AdminWhatsAppCoexistence from "./pages/admin/WhatsAppCoexistence";
import AdminGoogleAds from "./pages/admin/GoogleAds";
import AdminMetaAds from "./pages/admin/MetaAds";
import AdminWebAnalytics from "./pages/admin/WebAnalytics";
import AdminEmailTemplates from "./pages/admin/EmailTemplates";
import AdminCampaigns from "./pages/AdminCampaigns";
import VoiceAiAdmin from "./pages/admin/VoiceAiAdmin";
import VoiceAiAdminDetail from "./pages/admin/VoiceAiAdminDetail";
import CarmenSim from "./pages/admin/CarmenSim";
import CarmenBrain from "./pages/admin/CarmenBrain";
import AdminPushBroadcast from "./pages/admin/PushBroadcast";
import EmailCampaigns from "./pages/EmailCampaigns";
import MyTestimonial from "./pages/MyTestimonial";
import PublicProfile from "./pages/PublicProfile";
import CookieBanner from "./components/CookieBanner";
import { initGA4 } from "./lib/analytics";

// Redirect component that preserves query params
function RedirectWithParams({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

// Panel según la sede: una escuela (módulo Clases) no ve el panel de mesas.
// Son componentes SEPARADOS a propósito: así cada panel ejecuta solo sus
// hooks y cambiar de sede desmonta uno y monta el otro sin líos.
function PanelRoute() {
  const { currentLocation } = useLocationContext();
  return currentLocation?.classesEnabled === true ? (
    <ClassesPanel />
  ) : (
    <Dashboard />
  );
}

function App() {
  useEffect(() => {
    return initGA4();
  }, []);

  return (
    <ToastProvider>
      <ConfirmProvider>
      <AuthProvider>
        <LocationProvider>
          <SidebarProvider>
            {/* Asistente guiado de configuración: vive por encima de las rutas
                para poder abrirse desde cualquier pantalla y sobrevivir a los
                cambios de página (27-sep-2026). */}
            <SetupWizardProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route
                path="/confirm-email-change"
                element={<ConfirmEmailChange />}
              />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/invite/:token" element={<AcceptInvitation />} />

              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<PanelRoute />} />
                <Route path="reservations" element={<Reservations />} />
                <Route
                  path="reservations/calendar"
                  element={<ReservationsCalendar />}
                />
                <Route path="customers" element={<Customers />} />
                <Route path="schedule" element={<Schedule />} />
                <Route path="tables" element={<TableMap />} />
                <Route
                  path="table-combinations"
                  element={<TableCombinations />}
                />
                <Route path="menus" element={<Menus />} />
                <Route path="classes" element={<ClassesPage />} />
                <Route path="services" element={<Services />} />
                <Route path="specialists" element={<Specialists />} />
                <Route
                  path="specialists/:id/schedule"
                  element={<SpecialistSchedule />}
                />
                <Route
                  path="appointment-rooms"
                  element={<AppointmentRooms />}
                />
                <Route
                  path="appointment-rooms/:id/schedule"
                  element={<AppointmentRoomSchedule />}
                />
                <Route path="appointments" element={<Appointments />} />
                <Route
                  path="appointments/settings"
                  element={<AppointmentSettings />}
                />
                <Route
                  path="appointments/guide"
                  element={<AppointmentsGuide />}
                />
                <Route
                  path="location-settings"
                  element={<LocationSettings />}
                />
                <Route path="subscription" element={<Subscription />} />
                <Route path="team" element={<Team />} />
                <Route path="settings" element={<Settings />} />
                <Route path="integration" element={<Integration />} />
                <Route path="notifications" element={<Notifications />} />
                <Route
                  path="notifications/history"
                  element={<NotificationsHistory />}
                />
                <Route path="voice-ai" element={<VoiceAI />} />
                <Route path="conversations" element={<Conversations />} />
                <Route path="campaigns" element={<EmailCampaigns />} />
                <Route path="help" element={<HelpCenter />} />
                <Route path="help/ticket/:id" element={<TicketDetail />} />
                {/* Redirect old google-reserve route to integration */}
                <Route
                  path="google-reserve"
                  element={<RedirectWithParams to="/integration" />}
                />
                {/* Redirect old google-contacts route to customers with query params */}
                <Route
                  path="google-contacts"
                  element={<RedirectWithParams to="/customers" />}
                />
                <Route
                  path="integraciones"
                  element={<RedirectWithParams to="/customers" />}
                />
                <Route path="locations" element={<Locations />} />
                <Route path="testimonial" element={<MyTestimonial />} />
                <Route path="public-profile" element={<PublicProfile />} />

                {/* Admin routes (only accessible by superadmin) */}
                <Route path="admin" element={<AdminRoute />}>
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="organizations" element={<Organizations />} />
                  <Route
                    path="organizations/:id"
                    element={<Organizations />}
                  />
                  <Route path="health" element={<ClientsHealth />} />
                  <Route path="plans" element={<Plans />} />
                  <Route path="users" element={<UsersAdmin />} />
                  <Route path="endusers" element={<EndUsersAdmin />} />
                  <Route
                    path="app-visibility"
                    element={<AppVisibilityAdmin />}
                  />
                  <Route
                    path="classes-module"
                    element={<AdminClassesModule />}
                  />
                  <Route
                    path="impersonation-logs"
                    element={<ImpersonationLogs />}
                  />
                  <Route
                    path="reservation-statuses"
                    element={<ReservationStatuses />}
                  />
                  <Route path="tickets" element={<AdminTickets />} />
                  <Route path="testimonials" element={<AdminTestimonials />} />
                  <Route
                    path="public-profiles"
                    element={<AdminPublicProfiles />}
                  />
                  <Route
                    path="public-profiles/:id"
                    element={<AdminPublicProfileDetail />}
                  />
                  <Route path="emails" element={<AdminEmails />} />
                  <Route path="whatsapp" element={<AdminWhatsApp />} />
                  <Route path="whatsapp-ai" element={<AdminWhatsAppAi />} />
                  <Route
                    path="whatsapp-coexistencia"
                    element={<AdminWhatsAppCoexistence />}
                  />
                  <Route path="soporte" element={<AdminPlatformSupport />} />
                  <Route path="redes-sociales" element={<AdminSocialNetworks />} />
                  <Route path="google-ads" element={<AdminGoogleAds />} />
                  <Route path="meta-ads" element={<AdminMetaAds />} />
                  <Route path="web-analytics" element={<AdminWebAnalytics />} />
                  <Route
                    path="email-templates"
                    element={<AdminEmailTemplates />}
                  />
                  <Route path="campaigns" element={<AdminCampaigns />} />
                  <Route
                    path="push-broadcast"
                    element={<AdminPushBroadcast />}
                  />
                  <Route path="voice-ai" element={<VoiceAiAdmin />} />
                  <Route
                    path="voice-ai/:locationId"
                    element={<VoiceAiAdminDetail />}
                  />
                  <Route path="carmen-sim" element={<CarmenSim />} />
                  <Route path="carmen-brain" element={<CarmenBrain />} />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
            <CookieBanner />
            </SetupWizardProvider>
          </SidebarProvider>
        </LocationProvider>
      </AuthProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}

export default App;
